import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import { localizeSpelling, usePropertyCountry } from '../utils/propertyCountry';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  deleted_at?: string | null;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface Faq {
  question?: string | null;
}

export function SitemapContent() {
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);
  const faqs = useCollectionBinding<Faq>('sitemapcontent.faqs', { collection: 'faqs' });
  const hasFaqs = (faqs || []).some((f) => f.question?.trim());
  const country = usePropertyCountry();

  const pages = useMemo(
    () =>
      Object.values(pagesData as Record<string, PageEntry>)
        .filter((p) => {
          if (p.visible === false || p.deleted_at) return false;
          if (p.path === '/sitemap' || p.path === '/thank-you' || p.path.includes('{{')) return false;
          if (p.path === '/virtual-tour' && !hasVirtualTours) return false;
          if (p.path === '/frequently-asked-questions' && !hasFaqs) return false;
          return true;
        })
        .sort((a, b) => a.position - b.position),
    [hasVirtualTours, hasFaqs],
  );

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-black rounded px-6 py-4 text-center font-body text-sm text-black hover:text-black hover:border-black transition-colors"
            >
              {localizeSpelling(page.name, country)}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'sitemapcontent.faqs': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
